import { useState, useCallback, useEffect, useRef } from 'react';
import {
  Tag,
  Plus,
  X,
  Check,
  Palette,
  Trash2,
  Edit2,
  Search,
  Save,
  AlertCircle,
} from 'lucide-react';
import { Button, Badge, Spinner } from '@centraoneplus/ui';
import { organizationService, type Organization } from '@services/customer.service';

interface OrganizationTag {
  name: string;
  color?: string;
  count?: number;
}

interface OrganizationTagsEditorProps {
  organizationId?: string;
  initialTags?: string[];
  onTagsChange?: (tags: string[]) => void;
  mode?: 'inline' | 'full'; // inline for quick editing, full for management view
  allowCreate?: boolean;
  showCounts?: boolean;
}

// Predefined tag colors
const TAG_COLORS = [
  { name: 'gray', bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-200', border: 'border-gray-300 dark:border-gray-700' },
  { name: 'red', bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
  { name: 'orange', bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300' },
  { name: 'yellow', bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' },
  { name: 'green', bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  { name: 'teal', bg: 'bg-teal-100', text: 'text-teal-700', border: 'border-teal-300' },
  { name: 'blue', bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  { name: 'indigo', bg: 'bg-indigo-100', text: 'text-indigo-700', border: 'border-indigo-300' },
  { name: 'purple', bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-300' },
  { name: 'pink', bg: 'bg-pink-100', text: 'text-pink-700', border: 'border-pink-300' },
];

export function OrganizationTagsEditor({
  organizationId,
  initialTags = [],
  onTagsChange,
  mode = 'inline',
  allowCreate = true,
  showCounts = false,
}: OrganizationTagsEditorProps) {
  const [tags, setTags] = useState<string[]>(initialTags);
  const [availableTags, setAvailableTags] = useState<OrganizationTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [editingTag, setEditingTag] = useState<string | null>(null);
  const [newTagName, setNewTagName] = useState('');
  const [selectedColor, setSelectedColor] = useState(TAG_COLORS[0]);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load available tags from API (aggregate of all org tags)
  useEffect(() => {
    const loadAvailableTags = async () => {
      try {
        // Get organizations to aggregate tags
        const response = await organizationService.getOrganizations({ limit: 100 });
        const tagCounts: Record<string, number> = {};

        response.data.forEach((org) => {
          org.tags?.forEach((tag) => {
            tagCounts[tag] = (tagCounts[tag] || 0) + 1;
          });
        });

        const aggregatedTags = Object.entries(tagCounts).map(([name, count]) => ({
          name,
          count,
        }));

        setAvailableTags(aggregatedTags.sort((a, b) => (b.count || 0) - (a.count || 0)));
      } catch {
        // Silently fail - available tags are optional
      }
    };

    loadAvailableTags();
  }, []);

  // Handle clicking outside dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredTags = availableTags.filter(
    (tag) =>
      tag.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
      !tags.includes(tag.name)
  );

  const handleAddTag = useCallback(
    async (tagName: string) => {
      const trimmed = tagName.trim().toLowerCase();
      if (!trimmed || tags.includes(trimmed)) return;

      const newTags = [...tags, trimmed];
      setTags(newTags);
      setSearchQuery('');
      setShowDropdown(false);

      if (organizationId) {
        setIsSaving(true);
        try {
          await organizationService.updateOrganization(organizationId, { tags: newTags });
        } catch (err) {
          setError('Failed to add tag');
          setTags(tags); // Revert
        } finally {
          setIsSaving(false);
        }
      }

      onTagsChange?.(newTags);
    },
    [tags, organizationId, onTagsChange]
  );

  const handleRemoveTag = useCallback(
    async (tagName: string) => {
      const newTags = tags.filter((t) => t !== tagName);
      setTags(newTags);

      if (organizationId) {
        setIsSaving(true);
        try {
          await organizationService.updateOrganization(organizationId, { tags: newTags });
        } catch (err) {
          setError('Failed to remove tag');
          setTags(tags); // Revert
        } finally {
          setIsSaving(false);
        }
      }

      onTagsChange?.(newTags);
    },
    [tags, organizationId, onTagsChange]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter' && searchQuery.trim()) {
        e.preventDefault();
        handleAddTag(searchQuery);
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
        setSearchQuery('');
      } else if (e.key === 'Backspace' && !searchQuery && tags.length > 0) {
        handleRemoveTag(tags[tags.length - 1]);
      }
    },
    [searchQuery, handleAddTag, handleRemoveTag, tags]
  );

  const getTagColor = (tagName: string): typeof TAG_COLORS[0] => {
    // Simple hash to consistently assign colors
    const hash = tagName.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return TAG_COLORS[hash % TAG_COLORS.length];
  };

  // Inline mode - compact tag editor
  if (mode === 'inline') {
    return (
      <div className="relative" ref={dropdownRef}>
        <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 min-h-[42px]">
          {tags.map((tag) => {
            const color = getTagColor(tag);
            return (
              <span
                key={tag}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm ${color.bg} ${color.text} ${color.border} border`}
              >
                <Tag className="h-3 w-3" />
                {tag}
                <button
                  onClick={() => handleRemoveTag(tag)}
                  className="ml-1 hover:bg-black/10 rounded-full p-0.5"
                  disabled={isSaving}
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            );
          })}

          <input
            ref={inputRef}
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setShowDropdown(true);
            }}
            onFocus={() => setShowDropdown(true)}
            onKeyDown={handleKeyDown}
            placeholder={tags.length === 0 ? 'Add tags...' : ''}
            className="flex-1 min-w-[100px] bg-transparent border-none outline-none text-sm text-gray-700 dark:text-gray-300 placeholder-gray-400 dark:placeholder-gray-500"
            disabled={isSaving}
          />

          {isSaving && <Spinner size="sm" />}
        </div>

        {/* Dropdown for suggestions */}
        {showDropdown && (searchQuery || filteredTags.length > 0) && (
          <div className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-48 overflow-y-auto">
            {searchQuery && !availableTags.some((t) => t.name === searchQuery.toLowerCase()) && allowCreate && (
              <button
                onClick={() => handleAddTag(searchQuery)}
                className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
              >
                <Plus className="h-4 w-4 text-green-500" />
                <span>Create "{searchQuery}"</span>
              </button>
            )}

            {filteredTags.map((tag) => {
              const color = getTagColor(tag.name);
              return (
                <button
                  key={tag.name}
                  onClick={() => handleAddTag(tag.name)}
                  className="w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <span className={`w-3 h-3 rounded-full ${color.bg}`} />
                    {tag.name}
                  </span>
                  {showCounts && tag.count !== undefined && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">{tag.count} orgs</span>
                  )}
                </button>
              );
            })}

            {searchQuery && filteredTags.length === 0 && !allowCreate && (
              <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">No matching tags</div>
            )}
          </div>
        )}

        {error && (
          <div className="mt-1 flex items-center gap-1 text-sm text-red-600">
            <AlertCircle className="h-4 w-4" />
            {error}
          </div>
        )}
      </div>
    );
  }

  // Full mode - tag management view
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Organization Tags
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Manage tags to categorize and filter organizations
          </p>
        </div>
        {allowCreate && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setEditingTag('new')}
          >
            <Plus className="h-4 w-4 mr-1" />
            New Tag
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search tags..."
          className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-gray-900 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500 text-sm"
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Tags List */}
      <div className="border border-gray-200 dark:border-gray-700 rounded-lg divide-y divide-gray-200 dark:divide-gray-700">
        {isLoading ? (
          <div className="p-8 flex items-center justify-center">
            <Spinner size="lg" />
          </div>
        ) : filteredTags.length === 0 && !searchQuery ? (
          <div className="p-8 text-center text-gray-500 dark:text-gray-400">
            <Tag className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No tags created yet</p>
            {allowCreate && (
              <p className="text-sm mt-1">Click "New Tag" to create your first tag</p>
            )}
          </div>
        ) : filteredTags.length === 0 ? (
          <div className="p-4 text-center text-gray-500 dark:text-gray-400">
            No tags matching "{searchQuery}"
          </div>
        ) : (
          filteredTags.map((tag) => {
            const color = getTagColor(tag.name);
            return (
              <div
                key={tag.name}
                className="flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50"
              >
                <div className="flex items-center gap-3">
                  <span className={`p-2 rounded-lg ${color.bg}`}>
                    <Tag className={`h-4 w-4 ${color.text}`} />
                  </span>
                  <div>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {tag.name}
                    </span>
                    {showCounts && tag.count !== undefined && (
                      <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                        ({tag.count} organization{tag.count !== 1 ? 's' : ''})
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEditingTag(tag.name)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg dark:text-gray-500"
                    title="Edit tag"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => {
                      if (confirm(`Delete tag "${tag.name}"? This will remove it from all organizations.`)) {
                        // Handle delete - would need bulk update API
                      }
                    }}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg dark:text-gray-500"
                    title="Delete tag"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Edit/Create Tag Modal */}
      {editingTag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              {editingTag === 'new' ? 'Create New Tag' : `Edit Tag "${editingTag}"`}
            </h4>

            <div className="space-y-4">
              {/* Tag Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tag Name
                </label>
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Enter tag name..."
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white text-gray-900 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500 text-sm"
                  autoFocus
                />
              </div>

              {/* Color Picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {TAG_COLORS.map((color) => (
                    <button
                      key={color.name}
                      onClick={() => setSelectedColor(color)}
                      className={`w-8 h-8 rounded-full ${color.bg} ${color.border} border-2 flex items-center justify-center transition-transform ${
                        selectedColor.name === color.name ? 'scale-110 ring-2 ring-primary-500 ring-offset-2' : ''
                      }`}
                    >
                      {selectedColor.name === color.name && (
                        <Check className={`h-4 w-4 ${color.text}`} />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Preview
                </label>
                <span
                  className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm ${selectedColor.bg} ${selectedColor.text} ${selectedColor.border} border`}
                >
                  <Tag className="h-3.5 w-3.5" />
                  {newTagName || 'Tag name'}
                </span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <Button
                variant="ghost"
                onClick={() => {
                  setEditingTag(null);
                  setNewTagName('');
                  setSelectedColor(TAG_COLORS[0]);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (newTagName.trim()) {
                    handleAddTag(newTagName);
                    setEditingTag(null);
                    setNewTagName('');
                    setSelectedColor(TAG_COLORS[0]);
                  }
                }}
                disabled={!newTagName.trim()}
              >
                <Save className="h-4 w-4 mr-1" />
                {editingTag === 'new' ? 'Create Tag' : 'Save Changes'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrganizationTagsEditor;
